import React from 'react';
import { Title, Loader, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import { VetService } from '../../hooks/VetService';
import { TableBody, Table, Icon, Grid, Paper, Button, TableContainer, Chip, Container } from '@material-ui/core';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  MedicalSupplyOrderStatus,
  MedicalSupplyOrderViewModel,
  VeterinaryOrderViewModel
} from '../../models/injection';
import { SupplyOrderTableHeader, SupplyOrderTableRow } from '../../components/MedicalSupplyOrders/component';
import { ConfirmSupplyOrderModal } from '../../components';
import { MedicalSuppliesService } from '../../hooks';
import { useNavigate, useParams } from 'react-router-dom';

const VeterinaryOrdersPage: React.FC<any> = () => {
  const classes = useStyles();
  const vetService = new VetService();
  const medicalService = new MedicalSuppliesService();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = React.useState(false);

  const [orderToConfirm, setOrderToConfirm] = React.useState({} as MedicalSupplyOrderViewModel);
  const [orders, setOrders] = React.useState([] as VeterinaryOrderViewModel[]);

  const loadMedicalSupplyOrders = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    vetService
      .getVetMedicalSupplyOrders(searchPage, rowsPerPage)
      .then((result) => {
        setOrders(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadMedicalSupplyOrders();
    }
  }, [loaded]);

  React.useEffect(() => {
    if (id) {
      medicalService
        .getMedicalSupplyOrderById(id)
        .then((order) => {
          openConfirmModal(order);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    }
  }, [id]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const renderActionCell = (order: MedicalSupplyOrderViewModel) => {
    return order.status === MedicalSupplyOrderStatus.Processing ? (
      <Button color="primary" onClick={() => openConfirmModal(order)}>
        {t('f-supp-order-confirm')}
      </Button>
    ) : (
      '-'
    );
  };

  const openConfirmModal = (order: MedicalSupplyOrderViewModel) => {
    // deep copy order object; hack: (react hook stack bug fix)
    setOrderToConfirm(JSON.parse(JSON.stringify(order)));
    setConfirmModalOpened(true);
  };

  const orderConfirmationCallback = (result: boolean) => {
    setConfirmModalOpened(false);

    if (result) {
      setLoaded(false);
    }

    if (id) {
      // clear current url id param
      navigate('/vet/orders');
    }
  };

  return (
    <>
      <Container maxWidth="lg">
        <Grid container spacing={2} direction="row">
          <Grid item md={12}>
            <Grid container spacing={1}>
              <Grid item md={12} id="vetOrders">
                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item id="pageIcon">
                    <Icon color="primary" fontSize="large">
                      {'shopping_cart'}
                    </Icon>
                  </Grid>

                  <Grid item>
                    <Title>{t('f-supp-order-history')}</Title>
                  </Grid>

                  <Grid item>
                    <Chip label={total} variant="outlined" />
                  </Grid>
                  <Grid item container alignItems="center" spacing={1} className={classes.headerRightSection}>
                    <Grid item>
                      <ReloadIconButton action={() => setLoaded(false)} />
                    </Grid>
                  </Grid>
                </Grid>

                {loaded && (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TablePaginationWrapper
                        rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      >
                        <TableContainer component={Paper} elevation={1}>
                          <Table>
                            <TableBody>
                              <SupplyOrderTableHeader mode="veterinary" />

                              {orders.map((order, index) => (
                                <SupplyOrderTableRow
                                  key={index}
                                  order={order}
                                  mode="veterinary"
                                  actionCellComponent={renderActionCell(order)}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TablePaginationWrapper>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>
        </Grid>
      </Container>

      <ConfirmSupplyOrderModal
        opened={confirmModalOpened}
        order={orderToConfirm}
        onModalAction={orderConfirmationCallback}
      />
    </>
  );
};

export default VeterinaryOrdersPage;
